import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../../shared/api/client';
import type { AppNotification } from '../../shared/api/types';
import { useLiveVersion } from '../../shared/live';
import { closeTrayNotification } from '../../shared/push/app-badge';
import { disablePush, enablePush, isPushOn, pushSupport, syncPush } from '../../shared/push/push';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

const when = (iso: string) => new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Bandeja de notificaciones (Motor 11). Abrir una la marca como leída. */
export function NotificationsPage() {
  const me = useMe();
  const navigate = useNavigate();
  const live = useLiveVersion((e) => e.type === 'notification' || e.kind === 'notifications.read' || e.kind === 'notifications.resolved');
  const inbox = useAsync((signal) => api.get<{ items: AppNotification[]; unread: number }>('/notifications/me', signal), [me.personId, live]);

  async function open(n: AppNotification) {
    if (!n.readAt && !n.resolvedAt) await api.post(`/notifications/me/${n.id}/read`).catch(() => undefined);
    closeTrayNotification(n.id);
    if (n.link) navigate(n.link);
    else inbox.reload();
  }

  async function readAll() {
    await api.post('/notifications/me/read-all');
    inbox.reload();
  }

  return (
    <div className="page page--narrow">
      <header className="page__header page__header--actions">
        <h1>Notificaciones</h1>
        {inbox.status === 'ready' && inbox.data.unread > 0 ? (
          <Button variant="secondary" onClick={readAll}>
            ✓ Marcar todas como leídas
          </Button>
        ) : null}
      </header>
      <Card>
        {inbox.status === 'loading' ? <Loading /> : null}
        {inbox.status === 'error' ? <ErrorState error={inbox.error} onRetry={inbox.reload} /> : null}
        {inbox.status === 'ready' && inbox.data.items.length === 0 ? <EmptyState title="No tienes notificaciones." /> : null}
        {inbox.status === 'ready' && inbox.data.items.length > 0 ? (
          <ul className="list">
            {inbox.data.items.map((n) => (
              <li key={n.id} className={`list__item notification${n.resolvedAt ? ' notification--resolved' : n.readAt ? '' : ' notification--unread'}`}>
                <button type="button" className="notification__button" onClick={() => open(n)}>
                  <strong>{n.title}</strong>
                  {n.body ? <span className="muted small">{n.body}</span> : null}
                  <span className="muted small">{when(n.createdAt)}</span>
                  {n.resolvedAt ? <span className="notification__resolved small">✓ {n.resolvedBy ? `Atendida por ${n.resolvedBy}` : 'Ya se atendió'}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
      {/* Primero la lista; los ajustes, al final y plegados. */}
      <details className="notif-settings">
        <summary>Ajustes de notificaciones</summary>
        <PushCard />
        <PreferencesCard />
      </details>
    </div>
  );
}

/** Activar o quitar las notificaciones en ESTE celular o computadora. */
export function PushCard({ prompt = false }: { prompt?: boolean }) {
  const me = useMe();
  const [dismissed, setDismissed] = useState(() => {
    try {
      // En Inicio, «Ahora no»; en el panel de avisos, «Descartar» (✕): cada uno se oculta en este aparato.
      return localStorage.getItem(prompt ? 'keora.push.prompt' : 'keora.push.card') === 'no';
    } catch {
      return false;
    }
  });
  const state = useAsync(async (signal) => {
    const { publicKey } = await api.get<{ publicKey: string | null }>('/notifications/push/key', signal);
    const support = pushSupport(publicKey);
    // Si este navegador ya dio permiso, queda suscrito solo (no hace falta invitar a activarlos).
    if (support === 'ok') await syncPush(me.personId);
    return { publicKey, support, on: support === 'ok' ? await isPushOn().catch(() => false) : false };
  }, [me.personId]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (dismissed || state.status !== 'ready' || state.data.support === 'server-off') return null;
  const { publicKey, support, on } = state.data;
  // En Inicio solo se invita a activarlos si este dispositivo puede y aún no los tiene.
  if (prompt && (on || support === 'unsupported')) return null;
  const dismiss = () => {
    try {
      localStorage.setItem(prompt ? 'keora.push.prompt' : 'keora.push.card', 'no');
    } catch {
      /* sin almacenamiento: solo se oculta ahora */
    }
    setDismissed(true);
  };

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      if (on) await disablePush({ turnOff: true });
      else await enablePush(publicKey!);
      state.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron activar las notificaciones.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Notificaciones en este celular"
      action={
        <button type="button" className="link-button card__dismiss" aria-label="Descartar este anuncio" title="Descartar" onClick={dismiss}>
          ✕
        </button>
      }
    >
      {error ? <Alert>{error}</Alert> : null}
      {support === 'ios-install' ? (
        <p className="small">
          En iPhone primero agrega KEORA a tu pantalla de inicio: toca <strong>Compartir</strong> → <strong>Agregar a inicio</strong>, abre KEORA desde ese ícono y vuelve aquí para
          activar las notificaciones.
        </p>
      ) : support === 'unsupported' ? (
        <p className="small muted">Este navegador no puede recibir notificaciones. Prueba con Chrome, Edge, Firefox o Safari actualizados.</p>
      ) : (
        <>
          <p className="small">
            {on
              ? 'Este celular recibe tus notificaciones aunque KEORA esté cerrada.'
              : 'Recibe en este celular o computadora tus notificaciones (asignaciones, oración, eventos, mensajes…) aunque KEORA esté cerrada.'}
          </p>
          <Button variant={on ? 'ghost' : 'primary'} busy={busy} onClick={() => void toggle()}>
            {on ? 'Desactivar notificaciones aquí' : 'Activar notificaciones'}
          </Button>
          {prompt ? (
            <Button variant="ghost" onClick={dismiss}>
              Ahora no
            </Button>
          ) : null}
        </>
      )}
    </Card>
  );
}

/** Notificaciones que cada quien puede apagar (p. ej. la reflexión del devocional cada mañana). */
function PreferencesCard() {
  const prefs = useAsync((signal) => api.get<{ items: { kind: string; label: string; on: boolean }[] }>('/notifications/me/preferences', signal).then((r) => r.items).catch(() => []), []);
  if (prefs.status !== 'ready' || prefs.data.length === 0) return null;
  return (
    <Card title="Qué notificaciones recibir">
      {prefs.data.map((p) => (
        <label key={p.kind} className="check">
          <input
            type="checkbox"
            checked={p.on}
            onChange={async (e) => {
              await api.put('/notifications/me/preferences', { kind: p.kind, on: e.target.checked }).catch(() => undefined);
              prefs.reload();
            }}
          />{' '}
          {p.label}
        </label>
      ))}
    </Card>
  );
}
